// src/pages/IncomingPage.js
// Tools waiting for me to accept (David, 10/29). Three ways to take them:
//   • Accept or Decline one at a time
//   • Accept all
//   • Scan to accept: scan the tools that actually arrived (✓ Correct /
//     ↻ Re-scan after each). At Finish, any not scanned are listed with a
//     warning, and can still be accepted, left waiting, or declined.
// The text for a Scan Multiple batch links here (/incoming).
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { getMyPendingTransfers, respondToTransfers, getItemByQR, getItemById, getItems } from '../services/api';
import MultiScanner, { qrIdFrom } from '../components/MultiScanner';

export default function IncomingPage() {
  const navigate = useNavigate();
  const { worker } = useContext(AuthContext);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [mode, setMode] = useState('list');        // 'list' | 'scan' | 'missing'
  const [scanned, setScanned] = useState([]);      // transfer_ids scanned

  const load = async () => {
    try {
      const res = await getMyPendingTransfers();
      setTransfers((res.data.transfers || []).filter((t) => t.direction === 'incoming'));
    } catch {
      setError('Could not load your incoming tools. Check your connection.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [worker]);

  // Grouped by who sent them (and the job), newest first.
  const groups = useMemo(() => {
    const m = new Map();
    for (const t of transfers) {
      const k = `${t.from_worker_id}|${t.to_location_id || ''}`;
      if (!m.has(k)) m.set(k, { key: k, from: t.from_name, job: t.to_location_name, list: [] });
      m.get(k).list.push(t);
    }
    return [...m.values()];
  }, [transfers]);

  const respond = async (ids, action, scannedIds = []) => {
    if (!ids.length) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const res = await respondToTransfers({ transfer_ids: ids, action, scanned_ids: scannedIds });
      const done = res.data.done || [], failed = res.data.failed || [];
      setMessage(`${action === 'accept' ? '✅ Accepted' : '↩ Declined'} ${done.length} tool${done.length === 1 ? '' : 's'}.`
        + (failed.length ? ` ${failed.length} couldn't be updated (${failed[0].error}).` : ''));
      setScanned([]);
      setMode('list');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  // ── Scan to accept ──────────────────────────────────────
  const findTransfer = (it) => transfers.find((t) => t.item_id === it.item_id);
  const lookup = async (text) => {
    const { qr, itemId } = qrIdFrom(text);
    const res = itemId ? await getItemById(itemId) : await getItemByQR(qr);
    return res.data.item;
  };
  const search = async (text) => {
    const q = text.toLowerCase();
    const mine = transfers.find((t) => (t.asset_tag || '').toLowerCase() === q || (t.item_name || '').toLowerCase().includes(q));
    if (mine) return (await getItemById(mine.item_id)).data.item;
    return ((await getItems({ search: text, limit: 1 })).data.items || [])[0] || null;
  };
  const check = (it) => {
    const t = findTransfer(it);
    if (!t) return { block: "This tool isn't in your incoming transfers." };
    if (scanned.includes(t.transfer_id)) return { block: 'Already scanned.' };
    return { warn: null };
  };
  const onCorrect = (it) => {
    const t = findTransfer(it);
    if (t) setScanned((prev) => (prev.includes(t.transfer_id) ? prev : [...prev, t.transfer_id]));
  };
  const notScanned = transfers.filter((t) => !scanned.includes(t.transfer_id));

  const header = (title, sub, onBack) => (
    <div className="screen-header">
      <button onClick={onBack} aria-label="Back" style={{ background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer' }}>
        <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <div>
        <h2>{title}</h2>
        {sub && <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{sub}</p>}
      </div>
    </div>
  );

  if (loading) return <div className="screen"><div className="loading"><div className="spinner" /><span>Loading...</span></div></div>;

  // ── Scanning ────────────────────────────────────────────
  if (mode === 'scan') return (
    <div className="screen">
      {header('Scan to Accept', `${scanned.length} of ${transfers.length} scanned`, () => { setMode('list'); setScanned([]); })}
      <div className="screen-content">
        <MultiScanner
          lookup={lookup}
          search={search}
          check={check}
          onCorrect={onCorrect}
          count={scanned.length}
          finishDisabled={scanned.length === 0}
          finishLabel={scanned.length === 0 ? 'Scan a tool to begin' : `Finish: ${scanned.length} of ${transfers.length} scanned`}
          onFinish={() => (notScanned.length ? setMode('missing') : respond(scanned, 'accept', scanned))}
        />
        <div className="card">
          {transfers.map((t) => (
            <p key={t.transfer_id} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '0.875rem',
              color: scanned.includes(t.transfer_id) ? 'var(--text-primary)' : 'var(--text-muted)' }}>
              {scanned.includes(t.transfer_id) ? '✓' : '○'} {t.item_name}{t.asset_tag ? ` · ${t.asset_tag}` : ''}
            </p>
          ))}
        </div>
      </div>
    </div>
  );

  // ── Finished scanning, some not scanned ─────────────────
  if (mode === 'missing') return (
    <div className="screen">
      {header('Not All Scanned', null, () => setMode('scan'))}
      <div className="screen-content">
        <div className="alert scan-popup-warn" style={{ marginBottom: 12 }}>
          ⚠ {notScanned.length} tool{notScanned.length === 1 ? ' was' : 's were'} not scanned. Make sure you have {notScanned.length === 1 ? 'it' : 'them'} before accepting.
        </div>
        <div className="card" style={{ marginBottom: 16 }}>
          {notScanned.map((t) => (
            <p key={t.transfer_id} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '0.875rem' }}>
              ○ {t.item_name}{t.asset_tag ? ` · ${t.asset_tag}` : ''} <span style={{ color: 'var(--text-muted)' }}>from {t.from_name}</span>
            </p>
          ))}
        </div>
        <button className="btn btn-primary" disabled={busy} onClick={() => respond(scanned, 'accept', scanned)}>
          Accept the {scanned.length} scanned; leave the rest waiting
        </button>
        <button className="btn btn-secondary" style={{ marginTop: 10 }} disabled={busy}
          onClick={() => respond(transfers.map((t) => t.transfer_id), 'accept', scanned)}>
          Accept all {transfers.length} anyway
        </button>
        <button className="btn btn-ghost" style={{ marginTop: 10 }} disabled={busy} onClick={() => setMode('scan')}>
          ← Keep scanning
        </button>
      </div>
    </div>
  );

  // ── List ────────────────────────────────────────────────
  return (
    <div className="screen">
      {header('Incoming Tools', transfers.length ? `${transfers.length} waiting for you` : null, () => navigate('/'))}
      <div className="screen-content">
        {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
        {message && <div className="alert alert-success" style={{ marginBottom: 12 }}>{message}</div>}

        {transfers.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize: '2.5rem' }}>📥</div>
            <p>Nothing waiting for you.</p>
            <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/')}>Back to Home</button>
          </div>
        ) : (
          <>
            <button className="btn btn-success" disabled={busy} style={{ marginBottom: 10 }}
              onClick={() => respond(transfers.map((t) => t.transfer_id), 'accept')}>
              ✓ Accept All ({transfers.length})
            </button>
            <button className="btn btn-primary" disabled={busy} style={{ marginBottom: 18 }}
              onClick={() => { setScanned([]); setMode('scan'); }}>
              📷 Scan to Accept
            </button>

            {groups.map((g) => (
              <div key={g.key} style={{ marginBottom: 16 }}>
                <p className="picker-group-title">From {g.from}{g.job ? ` · 📍 ${g.job}` : ''}</p>
                <div className="card">
                  {g.list.map((t) => (
                    <div key={t.transfer_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontWeight: 600, fontSize: '0.9rem' }}>{t.item_name}</p>
                        {t.asset_tag && <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t.asset_tag}</p>}
                      </div>
                      <button className="btn btn-success incoming-btn" disabled={busy} onClick={() => respond([t.transfer_id], 'accept')}>Accept</button>
                      <button className="btn btn-secondary incoming-btn" disabled={busy}
                        onClick={() => window.confirm(`Decline ${t.item_name}? It stays with ${t.from_name}.`) && respond([t.transfer_id], 'reject')}>
                        Decline
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
